import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import {
  getConfiguration,
  evictAllPages,
  getUser,
  getWishLists,
  ComponentContextProvider,
  ElementContextProvider,
} from "@ten24/slatwall-storefront-react-private/global";
import App from "./App";
import { ContentContextProvider } from "./contexts/ContentContext";
import { AccountBubble } from "./components/Header/AccountBubble";
import { MiniCart } from "./components/Header/MiniCart";
import { MultiSitePicker } from "./components/MultiSitePicker/MultiSitePicker";
import { Header } from "./components/Header/Header";
import { ProductDetailDisplay } from "./components/ProductDetailDisplay/ProductDetailDisplay";
import { AccountProfile } from "./components/Account/AccountProfile/AccountProfile";
import { ContentColumns } from "./components/ContentColumns/ContentColumns";
import { CheckoutSideBar } from "./components/Checkout/CheckoutSideBar";
import { SkuCard, ProductCard } from "./components/ProductCard/ProductCard";
import { ListingBanner } from "./components/ListingBanner/ListingBanner";
import { ShippingSlide } from "./components/Checkout/Fulfilment/Shipping";
import { PaymentSlide } from "./components/Checkout/Payment/Payment";
import { CreateAccount } from "./components/Account/CreateAccount/CreateAccount";
import { SearchBar } from "./components/Header/SearchBar";
import { Video } from "./components/Video/Video";
import { BrandSlider } from "./components/BrandSlider/BrandSlider";
import { ImageSlider } from "./components/CMS/ImageSlider/ImageSlider";
import { WhereToBuyLocation } from "./components/WhereToBuyLocation/WhereToBuyLocation";
import { ContentGrid } from "./components/ContentGrid/ContentGrid";
import { ContentCard } from "./components/CMS/ContentCard/ContentCard";
import { ActionBanner } from "./components/ActionBanner/ActionBanner";
import { Ribbon } from "./components/Ribbon/Ribbon";
import { FeatureBlock } from "./components/FeatureBlock/FeatureBlock";
import { ImageGallery } from "./components/ImageGallery/ImageGallery";
import { LatestNews } from "./components/Blog/LatestNews";
import { ContentSlider } from "./components/ContentSlider/ContentSlider";
import { SimpleTabs } from "./components/CMS/Tab/Tab";
import { ContentBlock } from "./components/ContentBlock/ContentBlock";
import { DynamicForm } from "./components/DynamicForm/DynamicForm";
import { LanguagePicker } from "./components/LanguagePicker/LanguagePicker";
import { ContentLayout } from "./components/CMS/ContentLayout/ContentLayout";
import { ColumnResource } from "./components/CMS/ColumnResource/ColumnResource";
import { Footer } from "./components/Footer/Footer";
import { SearchListing } from "./components/SearchListing/SearchListing";
import { BrandSearchListing } from "./components/SearchListing/BrandSearchListing";
import { CategorySearchListing } from "./components/SearchListing/CategorySearchListing";
import { SearchListingStack } from "./components/SearchListing/SearchListingStack";
import { Block } from "./components/CMS/Block/Block";
import { Body } from "./components/CMS/Body/Body";
import { DynamicProductListing } from "./components/DynamicProductListing/DynamicProductListing";
import { AccountOverview } from "./components/Account/AccountOverview/AccountOverview";
import { AccountAddresses } from "./components/Account/AccountAddresses/AccountAddresses";
import { CreateOrEditAccountAddress } from "./components/Account/AccountAddresses/CreateOrEditAccountAddress";
import { AccountCarts } from "./components/Account/AccountCarts/AccountCarts";
import { AccountOrderHistory } from "./components/Account/AccountOrderHistory/AccountOrderHistory";
import { AccountQuotes } from "./components/Account/AccountQuotes/AccountQuotes";
import { AccountQuoteDetail } from "./components/Account/AccountQuotes/AccountQuoteDetail";
import { AccountOrderDetail } from "./components/Account/AccountOrderDetail/AccountOrderDetail";
import { CreateOrEditAccountPaymentMethod } from "./components/Account/AccountPaymentMethods/CreateOrEditAccountPaymentMethod";
import { AccountPaymentMethods } from "./components/Account/AccountPaymentMethods/AccountPaymentMethods";
import { AccountSubscriptionOrders } from "./components/Account/AccountSubscriptionOrders/AccountSubscriptionOrders";
import { AccountSubscriptionOrderDetail } from "./components/Account/AccountSubscriptionOrders/AccountSubscriptionOrderDetail";
import { GiftCardList, GiftCardView } from "./components/Account/GiftCardManagement";
import { AccountFavorites } from "./components/Account/AccountFavorites/AccountFavorites";
import { UpdatePassword } from "./components/Account/UpdatePassword/UpdatePassword";
import { AccountImpersonation } from "./components/Account/AccountImpersonation/AccountImpersonation";
import GuestOrderConfirmation from "./pages/OrderConfirmation/GuestOrderConfirmation";
import { CartLineItem } from "./components/Cart/CartLineItem";
import { CartPromoBox } from "./components/Cart/CartPromoBox";
import { OrderSummary } from "./components/Checkout/OrderSummary";
import { MegaMenu } from "./components/Header/MegaMenu";
import { MainNavBar } from "./components/Header/MainNavBar";
import { MenuItem } from "./components/Header/MenuItem";
import { Row } from "./components/MarkupTags/MarkUpRow";
import { TabbedProductListing } from "./components/CMS/TabbedProductListing/TabbedProductListing";
import { Theme } from "./components/Theme/Theme";
import { HTMLcontent } from "./components/htmlContent/htmlContent";
import { FeatureBanners } from "./components/FeatureBanners/FeatureBanners";
import { ProductTypeSearchListing } from "./components/SearchListing/ProductTypeSearchListing";
import { BlogListBody } from "./components/Blog/BlogListBody";
import { AccountLogin } from "./components/Account/AccountLogin/AccountLogin";
import { Resources } from "./components/Resources/Resources";
import { SearchPreFilter } from "./components/SearchListing/SearchPreFilter";
import { CategoryList } from "./components/CategoryList/CategoryList";
import { ProductReview } from "./components/ProductReview/ProductReview";
import { ListingPagination } from "./components/Listing/ListingPagination";
import SubscribeForm from "./components/SubscribeForm";

//plugins
import { SpanConfigurator } from "./plugins/SpanConfigurator/SpanConfigurator";
import { BlackhawkCalculator } from "./plugins/Blackhawk/Blackhawk";
import OverviewDraftOrders from "./components/Account/AccountOverview/OverviewDraftOrders";
import OverviewRecentOrders from "./components/Account/AccountOverview/OverviewRecentOrders";
import { BulkOrder } from "./components/BulkOrder/BulkOrder";


const ReCaptchaProvider = ({ children }) => {
  const siteSetting = useSelector((state) => state.configuration.site.settings);
  if (!siteSetting.siteRecaptchaSiteKey) return children;

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteSetting.siteRecaptchaSiteKey} useRecaptchaNet>
      {children}
    </GoogleReCaptchaProvider>
  );
};

const AppSwitcher = () => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const themeKey = useSelector((state) => state.configuration.theme.themeKey);

  const [safeToLoad, setSafeToLoad] = useState(false);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (pathname.startsWith("/ssoLogin")) {
      const authCode = new URLSearchParams(search).get("token");
      const redirect = new URLSearchParams(search).get("redirect") || "/my-account/overview";
      localStorage.setItem("token", authCode);
      dispatch(evictAllPages());
      dispatch(getUser()).then(() => {
        dispatch(getWishLists());
        navigate(redirect);
        toast.success(t("frontend.account.auth.success"));
        setSafeToLoad(true);
      });
    } else {
      setSafeToLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (safeToLoad) {
      dispatch(getConfiguration()).then((response) => {
        setConfigurationLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeToLoad]);
  // console.log(useSelector(state => state.configuration.site))

  // load and map the current components
  const customComponentMap = {
    cetMenu: MainNavBar,
    cetMegaMenu: MegaMenu,
    cetProductListing: DynamicProductListing,
    cetBlock: Block,
    cetFooter: Footer,
    cetContentLayout: ContentLayout,
    cetSearchListing: SearchListing,
    cetMenuItem: MenuItem,
    cetColumnResource: ColumnResource,
    cetForm: DynamicForm,
    cetColumn: ContentBlock,
    cetTabs: SimpleTabs,
    cetContentSlider: ContentSlider,
    cetBlogListing: LatestNews,
    cetImageGallery: ImageGallery,
    cetFeatureBlock: FeatureBlock,
    cetRibbon: Ribbon,
    cetContentCard: ContentCard,
    cetContentGrid: ContentGrid,
    cetImageSlider: ImageSlider,
    cetBrandListing: BrandSlider,
    cetVideo: Video,
    cetAccountLink: AccountBubble,
    cetCartLink: MiniCart,
    cetSiteSelector: MultiSitePicker,
    cetHeader: Header,
    cetColumns: ContentColumns,
    cetProductDetail: ProductDetailDisplay,
    cetSearchBox: SearchBar,
    cetWhereToBuy: WhereToBuyLocation,
    cetCallToCaction: ActionBanner,
    cetLanguageSelector: LanguagePicker,
    cetBody: Body,
    cetRow: Row,
    cetTabbedProductListing: TabbedProductListing,
    cetHTMLcontent: HTMLcontent,
    cetFeatureBanners: FeatureBanners,
    cetBlackhawkCalculator: BlackhawkCalculator,
    cetSpanConfigurator: SpanConfigurator,
    cetResources: Resources,
    cetPreFilters: SearchPreFilter,
    cetProductReview: ProductReview,
    cetAccountDraftOrders: OverviewDraftOrders,
    cetAccountRecentOrders: OverviewRecentOrders,
    cetSubscribeForm: SubscribeForm,
    cetBulkOrderListing: BulkOrder,
  };

  const customElementMap = {
    OrderSummary,
    CartPromoBox,
    CartLineItem,
    AccountOrderDetail,
    AccountAddresses,
    CreateOrEditAccountAddress,
    AccountCarts,
    AccountOrderHistory,
    AccountQuotes,
    AccountQuoteDetail,
    AccountOverview,
    CreateOrEditAccountPaymentMethod,
    AccountPaymentMethods,
    AccountSubscriptionOrders,
    AccountSubscriptionOrderDetail,
    GiftCardList,
    GiftCardView,
    AccountFavorites,
    UpdatePassword,
    AccountImpersonation,
    GuestOrderConfirmation,
    SearchListingStack,
    BrandSearchListing,
    CategorySearchListing,
    CategoryList,
    ProductTypeSearchListing,
    AccountProfile,
    SkuCard,
    ProductCard,
    ListingBanner,
    CheckoutSideBar,
    ShippingSlide,
    PaymentSlide,
    CreateAccount,
    HTMLcontent,
    FeatureBanners,
    BlogListBody,
    AccountLogin,
    ListingPagination
  };

  if (safeToLoad && configurationLoaded)
    return (
      <ElementContextProvider customComponents={customElementMap}>
        <ComponentContextProvider customComponents={customComponentMap}>
          <ContentContextProvider>
            <div className={`${themeKey}`}>
              <Theme>
                <ReCaptchaProvider>
                  <App />
                </ReCaptchaProvider>
              </Theme>
            </div>
          </ContentContextProvider>
        </ComponentContextProvider>
      </ElementContextProvider>
    );
  return null;
};

export { AppSwitcher };
